import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faDollarSign, 
  faLink, 
  faCopy, 
  faCalendarAlt,
  faChartLine,
  faEye,
  faSpinner,
  faPlus,
  faEnvelope,
  faUserTie,
  faTimes,
  faCheck,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

function AffiliateDashboard() {
  const { currentUser } = useContext(AuthContext);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [affiliateData, setAffiliateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [personalizedForm, setPersonalizedForm] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState({ type: '', text: '' });
  const [inviteForm, setInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isAdmin: false,
    campaignName: 'Team Member',
    commissionRate: ''
  });
  const [affiliateDefaultRate, setAffiliateDefaultRate] = useState(0);

  useEffect(() => {
    console.log('AffiliateDashboard useEffect triggered');
    console.log('currentUser:', currentUser);
    
    if (!currentUser) {
      console.log('No currentUser found');
      return;
    }
    
    const isAffiliate = currentUser.isAffiliate === 'True' || currentUser.isAffiliate === true || currentUser.Roles === 'Affiliate';
    console.log('isAffiliate check result:', isAffiliate);
    console.log('currentUser.isAffiliateAdmin:', currentUser.isAffiliateAdmin);
    
    if (!isAffiliate) {
      console.log('User is not an affiliate, exiting');
      return;
    }
    
    console.log('User is affiliate, calling fetchAffiliateData');
    fetchAffiliateData();
  }, [currentUser]);

  const setEmptyData = () => {
    console.log('Setting empty affiliate data - no backend data available');
    setAffiliateData({
      affiliate: { CompanyName: 'N/A', Status: 'Unknown' },
      refCode: 'N/A',
      referralCodes: [],
      totalVisits: 0,
      uniqueVisitors: 0,
      totalReferrals: 0,
      visitsLast30Days: 0,
      conversionsLast30Days: 0,
      totalEarnings: 0,
      totalPaid: 0,
      pendingEarnings: 0,
      conversionRate: 0,
      recentConversions: [],
      monthlyTrends: [],
      firstVisit: null,
      lastVisit: null,
      topCountries: null
    });
  };

  const fetchAffiliateData = async () => {
    console.log('Loading affiliate data for user:', currentUser.userId);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getAffiliateDashboardData',
          payload: {
            userId: currentUser.userId
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Real affiliate data loaded:', data);
        console.log('Backend returned userRole:', data.userRole);
        console.log('Frontend currentUser.isAffiliateAdmin:', currentUser.isAffiliateAdmin);
        
        // Transform API data to match component expectations
        console.log('About to set affiliateData with userRole:', data.userRole);
        
        setAffiliateData({
          affiliate: data.affiliate,
          refCode: data.userReferralCode?.ReferralCode || 'N/A',
          userReferralCode: data.userReferralCode, // User's specific assigned code
          allAffiliateCodes: data.allAffiliateCodes || [], // All codes for affiliate (admin view)
          userRole: data.userRole, // 'admin' or 'salesperson'
          totalVisits: data.analytics.visits.TotalVisits || 0,
          uniqueVisitors: data.analytics.visits.TotalUniqueVisitors || 0,
          totalReferrals: data.analytics.visits.TotalConversions || 0,
          visitsLast30Days: data.analytics.visits.VisitsLast30Days || 0,
          conversionsLast30Days: data.analytics.visits.ConversionsLast30Days || 0,
          totalEarnings: data.analytics.commissions.TotalEarned || 0,
          totalPaid: data.analytics.commissions.TotalPaid || 0,
          pendingEarnings: data.analytics.commissions.PendingPayout || 0,
          conversionRate: parseFloat(data.conversionRate || 0),
          recentConversions: data.recentConversions || [],
          monthlyTrends: data.analytics.monthlyTrends || [],
          firstVisit: data.analytics.visits.FirstVisit,
          lastVisit: data.analytics.visits.LastVisit,
          topCountries: data.analytics.visits.TopCountries
        });

        // Set the affiliate default commission rate for validation
        if (data.affiliate && data.affiliate.DefaultCommissionRate) {
          setAffiliateDefaultRate(parseFloat(data.affiliate.DefaultCommissionRate));
          console.log('Set affiliate default rate to:', data.affiliate.DefaultCommissionRate);
        } else {
          console.log('No DefaultCommissionRate found in affiliate data - using default 5%');
          setAffiliateDefaultRate(0.05); // Default to 5% if not found
        }
      } else {
        console.error('Failed to fetch affiliate data:', response.status);
        setEmptyData();
      }
    } catch (error) {
      console.error('Error fetching affiliate data:', error);
      setEmptyData();
    } finally {
      setLoading(false);
    }
  };

  const generateReferralLink = (campaign = '', additionalParams = {}) => {
    // Use current browser URL as base (preserves subdomain)
    const currentUrl = new URL(window.location.href);
    const baseUrl = `${currentUrl.protocol}//${currentUrl.host}`;
    
    const refCode = affiliateData?.refCode || 'N/A';
    
    // Don't generate links if no ref code available
    if (refCode === 'N/A') {
      return 'Referral code not available';
    }
    
    // Start with base URL and ref code
    const params = new URLSearchParams();
    params.set('ref', refCode);
    
    // Add campaign-specific UTM parameters
    if (campaign) {
      params.set('utm_source', 'affiliate');
      params.set('utm_campaign', campaign);
      params.set('utm_medium', 'referral');
      params.set('utm_term', affiliateData?.affiliate?.CompanyName?.replace(/\s+/g, '_').toLowerCase() || 'affiliate');
    }
    
    // Add any additional parameters
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    
    return `${baseUrl}/?${params.toString()}`;
  };

  const generatePersonalizedLink = (firstName = '', lastName = '', email = '') => {
    const personalParams = {};
    if (firstName) personalParams.firstName = firstName;
    if (lastName) personalParams.lastName = lastName;
    if (email) personalParams.email = email;
    
    return generateReferralLink('personalized', personalParams);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(`${type} copied!`);
      setTimeout(() => setCopySuccess(''), 3000);
    });
  };

  const fetchAffiliateDefaultRate = async () => {
    if (!currentUser || !currentUser.userId) return;
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://fuellinkapp.azurewebsites.net/api';
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getAffiliateApplication',
          payload: {
            userId: currentUser.userId
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('getAffiliateApplication response:', result);
        
        // Handle direct response (result is the affiliate data itself, or null)
        if (result && result.DefaultCommissionRate) {
          setAffiliateDefaultRate(parseFloat(result.DefaultCommissionRate));
        } else if (result === null) {
          console.log('No affiliate application found for user');
        }
        // Handle wrapped response format (fallback)
        else if (result && result.success && result.data && result.data.DefaultCommissionRate) {
          setAffiliateDefaultRate(parseFloat(result.data.DefaultCommissionRate));
        }
      } else {
        console.error('Failed to fetch affiliate application:', response.status);
      }
    } catch (error) {
      console.error('Error fetching affiliate default rate:', error);
    }
  };

  const isCommissionRateValid = (rateAsPercentage) => {
    const numRate = parseFloat(rateAsPercentage);
    const rateAsDecimal = numRate / 100;
    return !isNaN(numRate) && numRate >= 0 && rateAsDecimal <= affiliateDefaultRate;
  };

  const getCommissionRateError = (rateAsPercentage) => {
    const numRate = parseFloat(rateAsPercentage);
    if (isNaN(numRate) || numRate < 0) {
      return 'Commission rate must be a positive number';
    }
    const rateAsDecimal = numRate / 100;
    if (rateAsDecimal > affiliateDefaultRate) {
      return `Cannot exceed your affiliate rate of ${(affiliateDefaultRate * 100).toFixed(1)}%`;
    }
    return '';
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    if (!inviteForm.firstName || !inviteForm.lastName || !inviteForm.email) {
      setInviteMessage({ type: 'error', text: 'Please fill in all required fields.' });
      return;
    }

    // Validate phone number if provided
    if (inviteForm.phone && !validatePhone(inviteForm.phone)) {
      setInviteMessage({ type: 'error', text: 'Please enter a valid phone number in the format (123) 456-7890.' });
      return;
    }

    setInviteLoading(true);
    setInviteMessage({ type: '', text: '' });

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'inviteAffiliateMember',
          payload: {
            inviterUserId: currentUser.userId,
            memberFirstName: inviteForm.firstName,
            memberLastName: inviteForm.lastName,
            memberEmail: inviteForm.email,
            phone: inviteForm.phone,
            isAdmin: inviteForm.isAdmin,
            campaignName: inviteForm.campaignName || 'Team Member',
            commissionRate: inviteForm.commissionRate ? parseFloat(inviteForm.commissionRate) / 100 : null
          }
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setInviteMessage({ 
          type: 'success', 
          text: `Invitation sent successfully to ${inviteForm.firstName} ${inviteForm.lastName}! They will receive an email with referral code: ${result.data.referralCode}` 
        });
        
        // Reset form
        setInviteForm({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          isAdmin: false,
          campaignName: 'Team Member',
          commissionRate: ''
        });
        
        // Refresh affiliate data to show new team member
        setTimeout(() => {
          setShowInviteModal(false);
          fetchAffiliateData();
        }, 2000);
      } else {
        setInviteMessage({ 
          type: 'error', 
          text: result.error || 'Failed to send invitation. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      setInviteMessage({ 
        type: 'error', 
        text: 'Error sending invitation. Please check your connection and try again.' 
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (123) 456-7890
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length <= 10) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    } else {
      // Don't allow more than 10 digits
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setInviteForm({...inviteForm, phone: formatted});
  };

  const validatePhone = (phone) => {
    // Check if phone matches (123) 456-7890 format
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    return phoneRegex.test(phone) || phone === '';
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      isAdmin: false,
      campaignName: 'Team Member',
      commissionRate: ''
    });
    setInviteMessage({ type: '', text: '' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl mb-3 animate-spin" style={{ color: theme.primaryColor }} />
          <p>Loading affiliate dashboard...</p>
        </div>
      </div>
    );
  }

  if (!affiliateData) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading affiliate dashboard. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 
            className="text-2xl lg:text-3xl font-bold"
            style={{ color: theme.textPrimary || '#111827' }}
          >
            Affiliate Dashboard
          </h1>
          <p 
            className="text-sm lg:text-base"
            style={{ color: theme.textSecondary || '#6b7280' }}
          >
            {affiliateData.affiliate?.CompanyName && affiliateData.affiliate.CompanyName !== 'N/A' 
              ? `${affiliateData.affiliate.CompanyName} - ${currentUser.displayName || currentUser.email}`
              : `Welcome back, ${currentUser.displayName || currentUser.email}`
            }
          </p>
          {affiliateData.affiliate?.Status && (
            <div className="mt-1">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                affiliateData.affiliate.Status === 'ACTIVE' 
                  ? 'bg-green-100 text-green-800' 
                  : affiliateData.affiliate.Status === 'PENDING'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {affiliateData.affiliate.Status}
              </span>
            </div>
          )}
        </div>
        <div 
          className="rounded-lg shadow p-4"
          style={{ backgroundColor: theme.cardBG || '#ffffff' }}
        >
          <div 
            className="text-sm"
            style={{ color: theme.textSecondary || '#6b7280' }}
          >
            Your Ref Code
          </div>
          <div 
            className="font-mono text-lg font-bold"
            style={{ 
              color: affiliateData.refCode === 'N/A' 
                ? theme.textDisabled || '#9ca3af' 
                : theme.primaryColor || '#2563eb' 
            }}
          >
            {affiliateData.refCode}
          </div>
          {affiliateData.refCode === 'N/A' ? (
            <div className="text-xs text-red-500 mt-1">No referral code assigned</div>
          ) : (
            <div className="mt-2">
              {affiliateData.userReferralCode && (
                <div className="text-xs text-gray-600">
                  <div><strong>Campaign:</strong> {affiliateData.userReferralCode.CampaignName}</div>
                  {affiliateData.userReferralCode.CurrentUses > 0 && (
                    <div><strong>Uses:</strong> {affiliateData.userReferralCode.CurrentUses}</div>
                  )}
                  <div><strong>Status:</strong> 
                    <span className={`ml-1 ${affiliateData.userReferralCode.IsActive ? 'text-green-600' : 'text-red-600'}`}>
                      {affiliateData.userReferralCode.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          className="rounded-lg shadow p-4 lg:p-6"
          style={{ backgroundColor: theme.cardBG || '#ffffff' }}
        >
          <div className="flex items-center">
            <FontAwesomeIcon 
              icon={faEye} 
              className="text-xl lg:text-2xl mr-3" 
              style={{ color: theme.primaryColor || '#3b82f6' }}
            />
            <div>
              <div 
                className="text-xl lg:text-2xl font-bold"
                style={{ color: theme.textPrimary || '#111827' }}
              >
                {affiliateData.totalVisits}
              </div>
              <div 
                className="text-sm"
                style={{ color: theme.textSecondary || '#6b7280' }}
              >
                Total Visits
              </div>
              <div 
                className="text-xs"
                style={{ color: theme.textTertiary || '#9ca3af' }}
              >
                {affiliateData.uniqueVisitors} unique
              </div>
            </div>
          </div>
        </div>
        
        <div 
          className="rounded-lg shadow p-4 lg:p-6"
          style={{ backgroundColor: theme.cardBG || '#ffffff' }}
        >
          <div className="flex items-center">
            <FontAwesomeIcon 
              icon={faUsers} 
              className="text-xl lg:text-2xl mr-3"
              style={{ color: '#10b981' }}
            />
            <div>
              <div 
                className="text-xl lg:text-2xl font-bold"
                style={{ color: theme.textPrimary || '#111827' }}
              >
                {affiliateData.totalReferrals}
              </div>
              <div 
                className="text-sm"
                style={{ color: theme.textSecondary || '#6b7280' }}
              >
                Conversions
              </div>
              <div 
                className="text-xs"
                style={{ color: theme.textTertiary || '#9ca3af' }}
              >
                {affiliateData.conversionsLast30Days} this month
              </div>
            </div>
          </div>
        </div>
        
        <div 
          className="rounded-lg shadow p-4 lg:p-6"
          style={{ backgroundColor: theme.cardBG || '#ffffff' }}
        >
          <div className="flex items-center">
            <FontAwesomeIcon 
              icon={faDollarSign} 
              className="text-xl lg:text-2xl mr-3"
              style={{ color: '#059669' }}
            />
            <div>
              <div 
                className="text-xl lg:text-2xl font-bold"
                style={{ color: theme.textPrimary || '#111827' }}
              >
                ${affiliateData.totalEarnings?.toFixed(2) || '0.00'}
              </div>
              <div 
                className="text-sm"
                style={{ color: theme.textSecondary || '#6b7280' }}
              >
                Total Earnings
              </div>
              <div 
                className="text-xs"
                style={{ color: theme.textTertiary || '#9ca3af' }}
              >
                ${affiliateData.totalPaid?.toFixed(2) || '0.00'} paid
              </div>
            </div>
          </div>
        </div>
        
        <div 
          className="rounded-lg shadow p-4 lg:p-6"
          style={{ backgroundColor: theme.cardBG || '#ffffff' }}
        >
          <div className="flex items-center">
            <FontAwesomeIcon 
              icon={faChartLine} 
              className="text-xl lg:text-2xl mr-3"
              style={{ color: '#f59e0b' }}
            />
            <div>
              <div 
                className="text-xl lg:text-2xl font-bold"
                style={{ color: theme.textPrimary || '#111827' }}
              >
                {affiliateData.conversionRate}%
              </div>
              <div 
                className="text-sm"
                style={{ color: theme.textSecondary || '#6b7280' }}
              >
                Conversion Rate
              </div>
              <div 
                className="text-xs"
                style={{ color: theme.textTertiary || '#9ca3af' }}
              >
                visits to signups
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* This Month Stats */}
      <div 
        className="rounded-lg shadow p-4 lg:p-6"
        style={{ backgroundColor: theme.cardBG || '#ffffff' }}
      >
        <h2 
          className="text-lg lg:text-xl font-semibold mb-4"
          style={{ color: theme.textPrimary || '#111827' }}
        >
          Last 30 Days
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center">
            <div 
              className="text-2xl lg:text-3xl font-bold"
              style={{ color: theme.primaryColor || '#2563eb' }}
            >
              {affiliateData.visitsLast30Days}
            </div>
            <div 
              className="text-sm lg:text-base"
              style={{ color: theme.textSecondary || '#6b7280' }}
            >
              Visits
            </div>
          </div>
          <div className="text-center">
            <div 
              className="text-2xl lg:text-3xl font-bold"
              style={{ color: '#16a34a' }}
            >
              {affiliateData.conversionsLast30Days}
            </div>
            <div 
              className="text-sm lg:text-base"
              style={{ color: theme.textSecondary || '#6b7280' }}
            >
              Conversions
            </div>
          </div>
          <div className="text-center">
            <div 
              className="text-2xl lg:text-3xl font-bold"
              style={{ color: '#ea580c' }}
            >
              ${affiliateData.pendingEarnings?.toFixed(2) || '0.00'}
            </div>
            <div 
              className="text-sm lg:text-base"
              style={{ color: theme.textSecondary || '#6b7280' }}
            >
              Pending Payout
            </div>
          </div>
        </div>
        
        {affiliateData.topCountries && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-sm text-gray-600">
              <strong>Top Countries:</strong> {affiliateData.topCountries}
            </div>
            {affiliateData.firstVisit && (
              <div className="text-sm text-gray-600 mt-1">
                <strong>First Visit:</strong> {new Date(affiliateData.firstVisit).toLocaleDateString()}
                {affiliateData.lastVisit && (
                  <span> | <strong>Latest:</strong> {new Date(affiliateData.lastVisit).toLocaleDateString()}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Referral Visits Analytics */}
      <div 
        className="rounded-lg shadow p-4 lg:p-6"
        style={{ backgroundColor: theme.cardBG || '#ffffff' }}
      >
        <h2 
          className="text-lg lg:text-xl font-semibold mb-4"
          style={{ color: theme.textPrimary || '#111827' }}
        >
          <FontAwesomeIcon icon={faEye} className="mr-2" style={{ color: theme.primaryColor || '#3b82f6' }} />
          Referral Visits Analytics
        </h2>
        <p 
          className="text-sm mb-4"
          style={{ color: theme.textSecondary || '#6b7280' }}
        >
          Detailed insights from visitors using your referral codes
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Visits Last 30 Days */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div 
                  className="text-2xl font-bold text-blue-600"
                >
                  {affiliateData.visitsLast30Days}
                </div>
                <div className="text-sm text-blue-600 font-medium">
                  Visits This Month
                </div>
              </div>
              <FontAwesomeIcon icon={faCalendarAlt} className="text-2xl text-blue-400" />
            </div>
          </div>

          {/* Unique Visitors */}
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div 
                  className="text-2xl font-bold text-green-600"
                >
                  {affiliateData.uniqueVisitors}
                </div>
                <div className="text-sm text-green-600 font-medium">
                  Unique Visitors
                </div>
              </div>
              <FontAwesomeIcon icon={faUsers} className="text-2xl text-green-400" />
            </div>
          </div>

          {/* Conversion Rate */}
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div 
                  className="text-2xl font-bold text-purple-600"
                >
                  {affiliateData.conversionRate}%
                </div>
                <div className="text-sm text-purple-600 font-medium">
                  Conversion Rate
                </div>
              </div>
              <FontAwesomeIcon icon={faChartLine} className="text-2xl text-purple-400" />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div 
                  className="text-2xl font-bold text-orange-600"
                >
                  {affiliateData.conversionsLast30Days}
                </div>
                <div className="text-sm text-orange-600 font-medium">
                  Signups This Month
                </div>
              </div>
              <FontAwesomeIcon icon={faUsers} className="text-2xl text-orange-400" />
            </div>
          </div>
        </div>

        {/* Visit Details */}
        {(affiliateData.topCountries || affiliateData.firstVisit) && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {affiliateData.topCountries && (
                <div>
                  <h3 
                    className="text-sm font-semibold mb-2"
                    style={{ color: theme.textPrimary || '#111827' }}
                  >
                    Top Visitor Countries
                  </h3>
                  <p 
                    className="text-sm bg-gray-50 rounded-lg p-3"
                    style={{ color: theme.textSecondary || '#6b7280' }}
                  >
                    {affiliateData.topCountries}
                  </p>
                </div>
              )}
              {affiliateData.firstVisit && (
                <div>
                  <h3 
                    className="text-sm font-semibold mb-2"
                    style={{ color: theme.textPrimary || '#111827' }}
                  >
                    Visit Timeline
                  </h3>
                  <div className="text-sm space-y-1">
                    <div 
                      className="flex justify-between bg-gray-50 rounded p-2"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      <span>First Visit:</span>
                      <span className="font-medium">
                        {new Date(affiliateData.firstVisit).toLocaleDateString()}
                      </span>
                    </div>
                    {affiliateData.lastVisit && (
                      <div 
                        className="flex justify-between bg-gray-50 rounded p-2"
                        style={{ color: theme.textSecondary || '#6b7280' }}
                      >
                        <span>Latest Visit:</span>
                        <span className="font-medium">
                          {new Date(affiliateData.lastVisit).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Referral Code Performance (Admin Only) */}
      {affiliateData.userRole === 'admin' && affiliateData.codePerformance && affiliateData.codePerformance.length > 0 && (
        <div 
          className="rounded-lg shadow p-4 lg:p-6"
          style={{ backgroundColor: theme.cardBG || '#ffffff' }}
        >
          <h2 
            className="text-lg lg:text-xl font-semibold mb-4"
            style={{ color: theme.textPrimary || '#111827' }}
          >
            <FontAwesomeIcon icon={faUsers} className="mr-2" style={{ color: theme.primaryColor || '#3b82f6' }} />
            Salesperson Performance by Referral Code
          </h2>
          <p 
            className="text-sm mb-6"
            style={{ color: theme.textSecondary || '#6b7280' }}
          >
            Track which referral codes are driving the most visits and conversions
          </p>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code / Salesperson
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Visits
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    This Month
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversions
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conv. Rate
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {affiliateData.codePerformance.map((codeData, index) => (
                  <tr key={codeData.ReferralCode || index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div 
                          className="text-sm font-mono font-medium"
                          style={{ color: theme.textPrimary || '#111827' }}
                        >
                          {codeData.ReferralCode}
                        </div>
                        <div 
                          className="text-xs"
                          style={{ color: theme.textSecondary || '#6b7280' }}
                        >
                          {codeData.SalespersonName || 'Unassigned'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm" style={{ color: theme.textSecondary || '#6b7280' }}>
                      {codeData.CampaignName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium" style={{ color: theme.textPrimary || '#111827' }}>
                        {codeData.TotalVisits || 0}
                      </div>
                      <div className="text-xs text-gray-500">
                        {codeData.UniqueVisitors || 0} unique
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-blue-600">
                        {codeData.VisitsLast30Days || 0}
                      </div>
                      <div className="text-xs text-blue-500">
                        {codeData.UniqueVisitorsLast30Days || 0} unique
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-600">
                        {codeData.TotalConversions || 0}
                      </div>
                      <div className="text-xs text-green-500">
                        {codeData.ConversionsLast30Days || 0} this month
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        (codeData.ConversionRate || 0) >= 10
                          ? 'bg-green-100 text-green-800'
                          : (codeData.ConversionRate || 0) >= 5
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {parseFloat(codeData.ConversionRate || 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        codeData.IsActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {codeData.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-xs" style={{ color: theme.textSecondary || '#6b7280' }}>
                      {codeData.LastVisit ? (
                        <div>
                          <div>Last: {new Date(codeData.LastVisit).toLocaleDateString()}</div>
                          {codeData.FirstVisit && (
                            <div>First: {new Date(codeData.FirstVisit).toLocaleDateString()}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">No visits yet</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Performance Summary */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm font-medium text-blue-600 mb-1">Top Performer (Visits)</div>
                <div className="text-lg font-bold text-blue-700">
                  {affiliateData.codePerformance[0]?.SalespersonName || 'N/A'}
                </div>
                <div className="text-xs text-blue-500">
                  {affiliateData.codePerformance[0]?.TotalVisits || 0} total visits
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm font-medium text-green-600 mb-1">Top Converter</div>
                <div className="text-lg font-bold text-green-700">
                  {affiliateData.codePerformance
                    .sort((a, b) => (b.TotalConversions || 0) - (a.TotalConversions || 0))[0]?.SalespersonName || 'N/A'}
                </div>
                <div className="text-xs text-green-500">
                  {affiliateData.codePerformance
                    .sort((a, b) => (b.TotalConversions || 0) - (a.TotalConversions || 0))[0]?.TotalConversions || 0} conversions
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-sm font-medium text-purple-600 mb-1">Best Rate</div>
                <div className="text-lg font-bold text-purple-700">
                  {affiliateData.codePerformance
                    .filter(code => (code.TotalVisits || 0) > 0)
                    .sort((a, b) => (b.ConversionRate || 0) - (a.ConversionRate || 0))[0]?.SalespersonName || 'N/A'}
                </div>
                <div className="text-xs text-purple-500">
                  {parseFloat(affiliateData.codePerformance
                    .filter(code => (code.TotalVisits || 0) > 0)
                    .sort((a, b) => (b.ConversionRate || 0) - (a.ConversionRate || 0))[0]?.ConversionRate || 0).toFixed(1)}% rate
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referral Links */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Your Referral Links</h2>
        {copySuccess && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {copySuccess}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">General Referral Link</label>
            <div className="flex">
              <input
                type="text"
                value={generateReferralLink()}
                readOnly
                className="flex-1 p-2 border border-gray-300 rounded-l-md bg-gray-50"
              />
              <button
                onClick={() => copyToClipboard(generateReferralLink(), 'Link')}
                className="px-4 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600"
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Social Media Campaign Link</label>
            <div className="flex">
              <input
                type="text"
                value={generateReferralLink('social_media')}
                readOnly
                className="flex-1 p-2 border border-gray-300 rounded-l-md bg-gray-50"
              />
              <button
                onClick={() => copyToClipboard(generateReferralLink('social_media'), 'Social media link')}
                className="px-4 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600"
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Campaign Link</label>
            <div className="flex">
              <input
                type="text"
                value={generateReferralLink('email')}
                readOnly
                className="flex-1 p-2 border border-gray-300 rounded-l-md bg-gray-50"
              />
              <button
                onClick={() => copyToClipboard(generateReferralLink('email'), 'Email campaign link')}
                className="px-4 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600"
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Personalized Link Generator */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-3">Personalized Link Generator</h3>
          <p className="text-sm text-gray-600 mb-4">Create custom links with prospect information for better tracking</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <input
              type="text"
              placeholder="First Name"
              value={personalizedForm.firstName}
              onChange={(e) => setPersonalizedForm({...personalizedForm, firstName: e.target.value})}
              className="p-2 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={personalizedForm.lastName}
              onChange={(e) => setPersonalizedForm({...personalizedForm, lastName: e.target.value})}
              className="p-2 border border-gray-300 rounded-md"
            />
            <input
              type="email"
              placeholder="Email Address"
              value={personalizedForm.email}
              onChange={(e) => setPersonalizedForm({...personalizedForm, email: e.target.value})}
              className="p-2 border border-gray-300 rounded-md"
            />
          </div>
          
          <div className="flex">
            <input
              type="text"
              value={generatePersonalizedLink(personalizedForm.firstName, personalizedForm.lastName, personalizedForm.email)}
              readOnly
              className="flex-1 p-2 border border-gray-300 rounded-l-md bg-white text-sm"
            />
            <button
              onClick={() => copyToClipboard(generatePersonalizedLink(personalizedForm.firstName, personalizedForm.lastName, personalizedForm.email), 'Personalized link')}
              className="px-4 py-2 bg-green-500 text-white rounded-r-md hover:bg-green-600"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>
        </div>
      </div>

      {/* Referral Codes Section - Different views for Admin vs Member */}
      {(() => {
        console.log('Checking admin condition:', {
          userRole: affiliateData.userRole,
          isAdmin: affiliateData.userRole === 'admin',
          allCodesLength: affiliateData.allAffiliateCodes.length,
          shouldShowAdminSection: affiliateData.userRole === 'admin' && affiliateData.allAffiliateCodes.length > 0
        });
        return null;
      })()}
      {affiliateData.userRole === 'admin' && affiliateData.allAffiliateCodes.length > 0 && (
        <div 
          className="rounded-lg shadow p-6"
          style={{ backgroundColor: theme.cardBG || '#ffffff' }}
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ color: theme.textPrimary || '#111827' }}
          >
            Team Referral Codes
          </h2>
          <div className="flex justify-between items-center mb-4">
            <p 
              className="text-sm"
              style={{ color: theme.textSecondary || '#6b7280' }}
            >
              All referral codes for your affiliate organization
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 rounded-lg font-medium flex items-center space-x-2 hover:opacity-90 transition-opacity"
              style={{ 
                backgroundColor: theme.primaryColor || '#3b82f6',
                color: theme.buttonTextColor || '#ffffff'
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Invite Team Member</span>
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {affiliateData.allAffiliateCodes.map((code, index) => (
                  <tr key={code.ReferralCode || index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {code.ReferralCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {code.CampaignName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {code.SalespersonName || 'Unassigned'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {code.CurrentUses || 0}{code.MaxUses ? `/${code.MaxUses}` : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        code.IsActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {code.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Member's Personal Referral Code Info */}
      {affiliateData.userRole !== 'admin' && affiliateData.userReferralCode && (
        <div 
          className="rounded-lg shadow p-6"
          style={{ backgroundColor: theme.cardBG || '#ffffff' }}
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ color: theme.textPrimary || '#111827' }}
          >
            Your Referral Code Details
          </h2>
          <p 
            className="text-sm mb-4"
            style={{ color: theme.textSecondary || '#6b7280' }}
          >
            Information about your assigned referral code
          </p>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Referral Code
                </label>
                <div 
                  className="text-lg font-mono font-bold"
                  style={{ color: theme.primaryColor || '#2563eb' }}
                >
                  {affiliateData.userReferralCode.ReferralCode}
                </div>
              </div>
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Campaign
                </label>
                <div 
                  className="text-lg"
                  style={{ color: theme.textPrimary || '#111827' }}
                >
                  {affiliateData.userReferralCode.CampaignName}
                </div>
              </div>
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Total Uses
                </label>
                <div 
                  className="text-lg"
                  style={{ color: theme.textPrimary || '#111827' }}
                >
                  {affiliateData.userReferralCode.CurrentUses || 0}
                  {affiliateData.userReferralCode.MaxUses ? `/${affiliateData.userReferralCode.MaxUses}` : ''}
                </div>
              </div>
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ color: theme.textSecondary || '#6b7280' }}
                >
                  Status
                </label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  affiliateData.userReferralCode.IsActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {affiliateData.userReferralCode.IsActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Conversions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Conversions</h2>
        {affiliateData.recentConversions && affiliateData.recentConversions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Signup Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account Balance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {affiliateData.recentConversions.map((conversion, index) => (
                  <tr key={conversion.ConvertedUserId || index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {conversion.FirstName && conversion.LastName 
                        ? `${conversion.FirstName} ${conversion.LastName}`
                        : conversion.Email || 'Anonymous'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {conversion.CompanyName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(conversion.VisitDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {[conversion.City, conversion.Region, conversion.Country].filter(Boolean).join(', ') || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {conversion.AccountBalance ? `$${parseFloat(conversion.AccountBalance).toFixed(2)}` : 'Pending'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FontAwesomeIcon icon={faUsers} className="text-4xl mb-3 text-gray-300" />
            <p>No conversions yet.</p>
            <p className="text-sm">Share your referral links to start tracking customer signups!</p>
          </div>
        )}
      </div>

      {/* Marketing Resources */}
      <div 
        className="rounded-lg shadow p-6"
        style={{ backgroundColor: theme.cardBG || '#ffffff' }}
      >
        <h2 
          className="text-xl font-semibold mb-4"
          style={{ color: theme.textPrimary || '#111827' }}
        >
          Marketing Resources
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative border border-gray-200 rounded-lg p-4 bg-gray-50">
            {/* Coming Soon Overlay */}
            <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center">
              <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                Coming Soon
              </span>
            </div>
            <h3 className="font-semibold mb-2 text-gray-400">Banner Images</h3>
            <p className="text-gray-400 text-sm mb-3">High-quality banners for your website</p>
            <button className="text-gray-400 text-sm font-medium cursor-not-allowed" disabled>
              Download Pack →
            </button>
          </div>
          <div className="relative border border-gray-200 rounded-lg p-4 bg-gray-50">
            {/* Coming Soon Overlay */}
            <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center">
              <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                Coming Soon
              </span>
            </div>
            <h3 className="font-semibold mb-2 text-gray-400">Email Templates</h3>
            <p className="text-gray-400 text-sm mb-3">Pre-written emails for your campaigns</p>
            <button className="text-gray-400 text-sm font-medium cursor-not-allowed" disabled>
              View Templates →
            </button>
          </div>
          <div className="relative border border-gray-200 rounded-lg p-4 bg-gray-50">
            {/* Coming Soon Overlay */}
            <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center">
              <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                Coming Soon
              </span>
            </div>
            <h3 className="font-semibold mb-2 text-gray-400">Social Media Kit</h3>
            <p className="text-gray-400 text-sm mb-3">Posts and graphics for social platforms</p>
            <button className="text-gray-400 text-sm font-medium cursor-not-allowed" disabled>
              Get Kit →
            </button>
          </div>
        </div>
      </div>

      {/* Invite Team Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: theme.cardBG || '#ffffff' }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 
                  className="text-lg font-semibold"
                  style={{ color: theme.textPrimary || '#111827' }}
                >
                  <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                  Invite Team Member
                </h3>
                <button
                  onClick={closeInviteModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Messages */}
              {inviteMessage.text && (
                <div className={`p-4 rounded-lg border mb-4 ${
                  inviteMessage.type === 'success' 
                    ? 'bg-green-50 border-green-200 text-green-800' 
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                  <div className="flex items-center">
                    <FontAwesomeIcon 
                      icon={inviteMessage.type === 'success' ? faCheck : faExclamationTriangle} 
                      className="mr-2" 
                    />
                    {inviteMessage.text}
                  </div>
                </div>
              )}

              <form onSubmit={handleInvite} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={inviteForm.firstName}
                      onChange={(e) => setInviteForm({...inviteForm, firstName: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.textSecondary || '#6b7280' }}
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={inviteForm.lastName}
                      onChange={(e) => setInviteForm({...inviteForm, lastName: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.textSecondary || '#6b7280' }}
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john.doe@example.com"
                  />
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.textSecondary || '#6b7280' }}
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={inviteForm.phone}
                    onChange={handlePhoneChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.textSecondary || '#6b7280' }}
                  >
                    Campaign Name
                  </label>
                  <input
                    type="text"
                    value={inviteForm.campaignName}
                    onChange={(e) => setInviteForm({...inviteForm, campaignName: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Team Member"
                  />
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.textSecondary || '#6b7280' }}
                  >
                    Commission Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={inviteForm.commissionRate}
                    onChange={(e) => setInviteForm({...inviteForm, commissionRate: e.target.value})}
                    className={`w-full p-2 border rounded-md focus:ring-2 focus:border-transparent ${
                      inviteForm.commissionRate && !isCommissionRateValid(inviteForm.commissionRate)
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="5.0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter commission as a percentage (e.g., 5.0 for 5%). Cannot exceed your affiliate commission rate of {(affiliateDefaultRate * 100).toFixed(1)}%. If exceeded, payment will be limited to the maximum amount your affiliate account earns.
                  </p>
                  {inviteForm.commissionRate && getCommissionRateError(inviteForm.commissionRate) && (
                    <p className="text-xs text-red-500 mt-1">
                      {getCommissionRateError(inviteForm.commissionRate)}
                    </p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isAdmin"
                    checked={inviteForm.isAdmin}
                    onChange={(e) => setInviteForm({...inviteForm, isAdmin: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label 
                    htmlFor="isAdmin" 
                    className="ml-2 block text-sm"
                    style={{ color: theme.textSecondary || '#6b7280' }}
                  >
                    <FontAwesomeIcon icon={faUserTie} className="mr-1" />
                    Grant admin privileges
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  Admin members can invite other team members and view all affiliate data
                </p>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeInviteModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: theme.primaryColor || '#3b82f6' }}
                  >
                    {inviteLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AffiliateDashboard;